/* eslint-disable react/display-name */
import React, { memo, useState } from 'react';
import styled from 'styled-components';
import LAYERS_SELECTED_IMG from './assets/layers-white.png';
import LAYERS_IMG from './assets/layers-grey.png';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { LegendToggle } from './legendToggle';
import { PanZoomControl } from './panZoomControl';
import { MapControlContainerType } from '../utilities/interfaces';
import { commonTooltipInlineStyle } from '../utilities/constants';

const enabledBackgroundColor = '#2e98e3';
const disabledBackgroundColor = '#FFFFFF';

const layerButtonTooltipId = 'layer-button-tool-tip';

export const MapControlContainer = memo(({
    zoomInButtonEnabled,
    zoomOutButtonEnabled,
    panToMarkerButtonEnabled,
    toggleZoomRegionButtonEnabled,
    legendUnits,
    enableLegend = true,
    showLegendToggleButton = true,
    showMapControlActionButton = true,
    layers,
    onLayerPressed,
    currentLayerId,
    currentZoomLevel,
    legendColors,
    legendLabels,
    alertsLegend,
    legendSets,
    layerPicker,
    zoomPicker,
    animationControl,
    legend,
    animationEnabled,
    isAnimating,
    animationTime,
    animationCallback,
    progressPercentage,
    mapPanToMarkerPressed,
    mapZoomInPressed,
    mapZoomOutPressed,
    enableLayerPicker,
    enableZoomPicker,
    onToggleLayerPicker,
    changeZoom,
    onToggleZoomPicker,
    adRefreshActionOnMapLegendToggleOff,
    adRefreshActionOnMapLegendToggleOn,
    adRefreshActionOnPresetZoom
}: MapControlContainerType) => {
    const [ showLegend, setShowLegend ] = useState(enableLegend);

    const mapLegendsTogglePressed = () => {
        if (showLegend) {
            adRefreshActionOnMapLegendToggleOff?.();
        } else {
            adRefreshActionOnMapLegendToggleOn?.();
        }
        setShowLegend(!showLegend);
    };

    const mapLayerTogglePressed = () => {
        if (onToggleLayerPicker) {
            onToggleLayerPicker(!enableLayerPicker);
        }
    };

    const mapToggleZoomRegionPressed = () => {
        if (onToggleZoomPicker) {
            onToggleZoomPicker(!enableZoomPicker);
        }
    };

    const changeZoomLevel = (zoomLevel: number) => {
        if (changeZoom) {
            changeZoom(zoomLevel);
            adRefreshActionOnPresetZoom?.(zoomLevel);
        }

        if (onToggleZoomPicker) {
            onToggleZoomPicker(!enableZoomPicker);
        }
    };

    return (
        <div id='mapControlContainer'>
            {showLegend && legend && legend(legendColors, legendLabels, alertsLegend, legendSets, legendUnits)}
            <MapControlOuterContainer id='mapLayerPicker'>
                {enableLayerPicker && layerPicker && layerPicker(layers, onLayerPressed, currentLayerId)}
                {enableZoomPicker && zoomPicker && zoomPicker(currentZoomLevel, changeZoomLevel)}
                <ReactTooltip 
                    style={commonTooltipInlineStyle} 
                    positionStrategy='fixed'
                    effect='solid'
                    content='Pick Layers'
                    id={layerButtonTooltipId} />
                <MapLayerPickerInnerContainer id='mapLayerPickerInnerContainer'>
                    {showMapControlActionButton && layers && layers.length > 0 && <>
                        <MapControlActionButton
                            showLayerPicker={enableLayerPicker}
                            enabledBackgroundColor={enabledBackgroundColor}
                            disabledBackgroundColor={disabledBackgroundColor}
                            onClick={() => mapLayerTogglePressed()}
                            data-tooltip-id={layerButtonTooltipId}>
                            <MapLayerPickerIcon alt='Map layer picker' id='mapLayerPickerIcon' className='mapIcon' src={enableLayerPicker ? LAYERS_SELECTED_IMG : LAYERS_IMG} />
                        </MapControlActionButton></>
                    }
                    {showLegendToggleButton && <LegendToggle
                        legendEnabled={showLegend}
                        toggleLegend={() => mapLegendsTogglePressed()}
                        showBorder={!!animationControl} />
                    }
                    {animationControl && animationControl(animationEnabled, animationTime, animationCallback, progressPercentage, isAnimating)}
                </MapLayerPickerInnerContainer>
            </MapControlOuterContainer>
            <div id='mapPanZoomContainer'>
                <PanZoomControl
                    zoomInPressed={mapZoomInPressed}
                    zoomOutPressed={mapZoomOutPressed}
                    panToMarkerPressed={mapPanToMarkerPressed}
                    toggleZoomRegionPressed={() => mapToggleZoomRegionPressed()}
                    zoomPickerEnabled={enableZoomPicker}
                    zoomInButtonEnabled={zoomInButtonEnabled}
                    zoomOutButtonEnabled={zoomOutButtonEnabled}
                    panToMarkerButtonEnabled={panToMarkerButtonEnabled}
                    toggleZoomRegionButtonEnabled={toggleZoomRegionButtonEnabled}
                />
            </div>
        </div>
    );
});

const MapControlOuterContainer = styled.div`
    display: flex;
`;

const MapLayerPickerInnerContainer = styled.div`
    display: flex;
    background-color: white;
    height: 46px;
    margin-top: 5px;
`;

interface MapControlActionButtonProps {
    showLayerPicker: boolean;
    enabledBackgroundColor: string;
    disabledBackgroundColor: string;
}
const MapControlActionButton = styled.div<MapControlActionButtonProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 93px;
    cursor: pointer;
    border-right: 1px solid ${({ showLayerPicker }) => showLayerPicker ? '#FFFFFF' : '#D8D8D8'};
    background-color: ${({ 
        showLayerPicker, 
        enabledBackgroundColor, 
        disabledBackgroundColor 
    }) => showLayerPicker ? enabledBackgroundColor : disabledBackgroundColor};
`;

const MapLayerPickerIcon = styled.img`
    max-height: 26px;
`;
